import { csvParse } from "d3-dsv";
import { ungzip } from "pako";
import { recordMindshareTiming, recordMindshareValue, timeMindshare } from "./mindsharePerformance";

export const SNAPSHOT_URL = "https://mindgamedataapi.s3.us-east-1.amazonaws.com/mindSHARE_cards/mindshare_cards.json.gz";
export const HISTORY_URL = "https://d1ct3zodbaiefe.cloudfront.net/game_stats.csv.gz";
export const GAME_HISTORY_BASE = "https://mindgamedataapi.s3.us-east-1.amazonaws.com";
export const GAME_HISTORY_MANIFEST_URL = `${GAME_HISTORY_BASE}/mindshare_history/manifest.json`;
export const FAVORITES_HISTORY_URLS = Object.freeze({
  top100: `${GAME_HISTORY_BASE}/mindSHARE_cards/favorites_top100.json.gz`,
  recent12m: `${GAME_HISTORY_BASE}/mindSHARE_cards/favorites_recent12m.json.gz`,
  upcoming: `${GAME_HISTORY_BASE}/mindSHARE_cards/favorites_upcoming.json.gz`,
});
export const SNAPSHOT_TTL = 5 * 60 * 1000;
const metadataFields = ["name", "matchKey", "metadataRow", "imageUrl", "publisher", "developer", "description", "tags", "genres", "platforms", "live_service_static", "pricing", "user_generated_content", "single_multiplayer_focus", "release_date"];
const cardMetadataFields = metadataFields.filter(key => !["matchKey", "metadataRow"].includes(key));
const observationFields = ["date", "w2l", "level", "rank", "searchRank", "videoRank", "twitchRank", "search"];
export const SNAPSHOT_COLUMNS = [...metadataFields, ...["latest", "previous"].flatMap(prefix => observationFields.map(field => `${prefix}_${field}`)), "observationCount", "launchScore"];
export const FAVORITES_HISTORY_COLUMNS = ["Game", "Period", "Weeks_to_Launch", "mindSHARE", "Growth", "Growth_PT", "Rank___mindSHARE", "Rank___Search", "Rank___Video", "Rank___Twitch", "Search_Volume", "Steam_Peak_Weekly", "Steam_Peak_All_Time", "year"];
export const FAVORITES_METADATA_COLUMNS = ["Game", "Released_Unreleased", "Release_Date", "Description", "Publisher", "Developer", "Genre", "PlayStation_5", "PlayStation_4", "PlayStation_Exclusive", "Xbox_Series_S_X", "Xbox_One", "Xbox_Exclusive", "Switch", "Switch_Exclusive", "Switch_2", "Switch_2_Exclusive", "PC", "PC_Exclusive", "Mobile", "Mobile_Exclusive", "Live_Service_Static", "Pricing_Monetization", "User_Generated_Content_(UGC)", "Single_Multiplayer_Focus", "Platform_Group_Filter", "ImageURL", "Steam_ID", "Tags"];
const numeric = value => Number.isFinite(+value) ? +value : null;
const normalize = (s = "") => s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[’'`]/g, "").replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
const normalizeFavoriteMatchKey = value => value.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[’'`\uFFFD]/g, "").replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
let snapshotCache;
let snapshotFlight;
let historyFlight;
let manifestFlight;
const favoritesCache = new Map();
const favoritesFlights = new Map();
const favoritesSourceVersions = new Map();
const gameHistoryFlights = new Map();
const gameHistoryIndexes = new WeakMap();
const metadataCache = new WeakMap();
const historyGamesCache = new WeakMap();
const modelCache = new WeakMap();
const filterCache = new WeakMap();
const filterCollator = new Intl.Collator(undefined, { sensitivity: "base" });

export async function readGzipResponse(response) {
  if (!response.ok) throw new Error(`mindSHARE data HTTP ${response.status}`);
  const start = performance.now();
  const bytes = new Uint8Array(await response.arrayBuffer());
  recordMindshareTiming("response-body", start);
  let raw = bytes;
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const gzipStart = performance.now();
    if (typeof DecompressionStream !== "undefined") {
      raw = new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer());
    } else {
      raw = ungzip(bytes);
    }
    recordMindshareTiming("gzip", gzipStart);
  }
  return timeMindshare("text-decode", () => new TextDecoder("utf-8").decode(raw).trim());
}

export function decodeSnapshot(data) {
  if (![1, 2].includes(data?.schemaVersion) || !Number.isInteger(data.rowCount) || data.rowCount <= 0 || !data.columns) {
    throw new Error("Unsupported or empty mindSHARE snapshot");
  }
  const columns = data.schemaVersion === 1 && data.columns.launchScore === undefined
    ? { ...data.columns, launchScore: Array(data.rowCount).fill(null) }
    : data.columns;
  for (const field of SNAPSHOT_COLUMNS) {
    if (!Array.isArray(columns[field]) || columns[field].length !== data.rowCount) {
      throw new Error(`Invalid mindSHARE snapshot column: ${field}`);
    }
  }
  return Array.from({ length: data.rowCount }, (_, i) => {
    const row = Object.fromEntries(SNAPSHOT_COLUMNS.map(field => [field, columns[field][i]]));
    for (const field of metadataFields) {
      const value = row[field];
      const valid = ["tags", "genres", "platforms"].includes(field)
        ? Array.isArray(value) && value.every(item => typeof item === "string")
        : field === "metadataRow" ? Number.isInteger(value)
        : field === "release_date" ? value === null || typeof value === "string"
        : typeof value === "string";
      if (!valid) throw new Error(`Invalid mindSHARE snapshot value: ${field}, row ${i}`);
    }
    row.name = row.name.trim();
    if (!row.name || !Number.isInteger(row.observationCount) || row.observationCount < 1) {
      throw new Error(`Invalid mindSHARE snapshot game at row ${i}`);
    }
    if (row.launchScore !== null && (typeof row.launchScore !== "number" || !Number.isFinite(row.launchScore))) {
      throw new Error(`Invalid launchScore at row ${i}`);
    }
    for (const prefix of ["latest", "previous"]) {
      for (const field of observationFields) {
        const value = row[`${prefix}_${field}`];
        const valid = field === "date"
          ? (prefix === "previous" && value === null) || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)))
          : value === null || (typeof value === "number" && Number.isFinite(value));
        if (!valid) throw new Error(`Invalid ${prefix}_${field} at row ${i}`);
      }
    }
    if (typeof row.latest_level !== "number") throw new Error(`Missing latest level at row ${i}`);
    return row;
  });
}

// Retains the prototype's card coercions, null ranks, ordering, and two-point tooltip lookups.
export function hydrateSnapshot(rows, now = new Date()) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const cached = modelCache.get(rows);
  if (cached?.day === today.getTime()) return cached.games;
  let firstRelease = null;
  let lastRelease = null;
  const games = rows.map(row => {
    const game = Object.fromEntries(cardMetadataFields.map(key => [key, row[key]]));
    const release = row.release_date ? new Date(row.release_date) : null;
    if (release && !isNaN(release)) {
      if (!firstRelease || release < firstRelease) firstRelease = release;
      if (!lastRelease || release > lastRelease) lastRelease = release;
    }
    Object.assign(game, {
      isUnreleased: !release || isNaN(release) ? true : release > today,
      views: Number(row.latest_level ?? 0),
      videos: row.previous_date !== null ? Number(row.latest_level - (row.previous_level ?? 0)) : 0,
      launch_score: row.launchScore,
      rank_mindshare: numeric(row.latest_rank), search_rank: numeric(row.latest_searchRank),
      video_rank: numeric(row.latest_videoRank), twitch_rank: numeric(row.latest_twitchRank),
      tiktok_rank: 0,
      _byDate: new Map(), _metaByDate: new Map(), _dateKeys: [],
    });
    for (const prefix of ["previous", "latest"]) {
      const date = row[`${prefix}_date`];
      if (date === null) continue;
      game._dateKeys.push(date);
      game._byDate.set(date, row[`${prefix}_level`]);
      game._metaByDate.set(date, {
        rank: row[`${prefix}_rank`], searchRank: row[`${prefix}_searchRank`],
        videoRank: row[`${prefix}_videoRank`], twitchRank: row[`${prefix}_twitchRank`],
        search: row[`${prefix}_search`],
      });
    }
    return game;
  }).sort((a, b) => (b.views || 0) - (a.views || 0));
  modelCache.set(rows, { day: today.getTime(), games, releaseDateRange: [firstRelease, lastRelease] });
  return games;
}

export function snapshotReleaseDateRange(rows) {
  hydrateSnapshot(rows);
  return modelCache.get(rows).releaseDateRange;
}

export function snapshotFilterOptions(rows) {
  if (filterCache.has(rows)) return filterCache.get(rows);
  const sets = Object.fromEntries(["tags", "genres", "publishers", "developers", "liveService", "pricing", "ugc", "focus", "platforms"].map(key => [key, new Set()]));
  const add = (key, values) => {
    for (const value of values) {
      const cleaned = String(value || "").trim();
      if (cleaned) sets[key].add(cleaned);
    }
  };
  // Match the original stable sort's tie ordering by visiting games in card order.
  for (const game of hydrateSnapshot(rows)) {
    add("tags", game.tags || []);
    add("genres", game.genres || []);
    add("publishers", [game.publisher]);
    add("developers", [game.developer]);
    add("liveService", [game.live_service_static]);
    add("pricing", [game.pricing]);
    add("ugc", [game.user_generated_content]);
    add("focus", String(game.single_multiplayer_focus || "").split(","));
    add("platforms", game.platforms || []);
  }
  const options = Object.fromEntries(Object.entries(sets).map(([key, values]) => [
    key, [...values].sort(filterCollator.compare).map(value => ({ label: value, value })),
  ]));
  filterCache.set(rows, options);
  return options;
}

export function snapshotMetadata(rows) {
  if (metadataCache.has(rows)) return metadataCache.get(rows);
  const metadata = rows.map(row => ({
    Game: row.name, ImageURL: row.imageUrl, Publisher: row.publisher, Developer: row.developer,
    Description: row.description, Tags: row.tags.join(","), Genre: row.genres.join(","),
    Platform_Group_Filter: row.platforms.join(","), Live_Service_Static: row.live_service_static,
    Pricing_Monetization: row.pricing, "User_Generated_Content_(UGC)": row.user_generated_content,
    Single_Multiplayer_Focus: row.single_multiplayer_focus, Release_Date: row.release_date || "",
  }));
  metadataCache.set(rows, metadata);
  return metadata;
}

export function cachedHistoryGames(index, metadata, build) {
  let entries = historyGamesCache.get(index);
  if (!entries) { entries = new WeakMap(); historyGamesCache.set(index, entries); }
  if (!entries.has(metadata)) entries.set(metadata, build());
  return entries.get(metadata);
}

export function loadSnapshot() {
  if (snapshotCache && Date.now() - snapshotCache.at < SNAPSHOT_TTL) return Promise.resolve(snapshotCache.rows);
  if (!snapshotFlight) {
    snapshotFlight = fetch(SNAPSHOT_URL, { mode: "cors", cache: "no-cache" })
      .then(readGzipResponse).then(text => {
        let rows = snapshotCache?.text === text ? snapshotCache.rows : null;
        if (!rows) {
          const data = timeMindshare("json-parse", () => JSON.parse(text));
          rows = timeMindshare("snapshot-validate", () => decodeSnapshot(data));
        }
        timeMindshare("model", () => hydrateSnapshot(rows));
        timeMindshare("filter-precompute", () => snapshotFilterOptions(rows));
        snapshotCache = { rows, text, at: Date.now() };
        return rows;
      })
      .finally(() => { snapshotFlight = undefined; });
  }
  return snapshotFlight;
}

export function buildHistoryIndex(rows) {
  const index = new Map();
  for (const row of rows) {
    const name = (row.Game ?? row.game ?? "").trim();
    if (!name) continue;
    const date = new Date(row.Period ?? row["Period (DATE)"] ?? row.period ?? row.Date ?? row.date ?? "");
    if (!Number.isFinite(date.getTime())) throw new Error(`Invalid mindSHARE history date for ${name}`);
    const level = Number(row.mindSHARE ?? row.MindSHARE ?? row.mindshare ?? row.Mindshare);
    if (!Number.isFinite(level)) continue;
    const key = normalize(name);
    if (!index.has(key)) index.set(key, []);
    index.get(key).push({
      date: date.toISOString().slice(0, 10), level, weekly: 0,
      w2l: numeric(row.Weeks_to_Launch ?? row["Weeks to Launch"] ?? row.weeks_to_launch ?? row.w2l ?? row.W2L ?? null),
      rank: numeric(row.Rank_mindSHARE ?? row.Rank__mindSHARE ?? row.Rank___mindSHARE),
      searchRank: numeric(row.Rank_Search ?? row.Rank__Search ?? row.Rank___Search),
      videoRank: numeric(row.Rank_Video ?? row.Rank__Video ?? row.Rank___Video),
      twitchRank: numeric(row.Rank_Twitch ?? row.Rank__Twitch ?? row.Rank___Twitch),
      search: numeric(row.Search_Volume ?? row.search_volume ?? null),
    });
  }
  for (const series of index.values()) {
    series.sort((a, b) => a.date < b.date ? -1 : 1);
    series.forEach((row, i) => { row.weekly = i ? row.level - series[i - 1].level : 0; });
  }
  return index;
}

export function snapshotLatestPeriod(rows) {
  return rows?.reduce((latest, row) => {
    const period = row.latest_date;
    return typeof period === "string" && /^\d{4}-\d{2}-\d{2}$/.test(period) && period > (latest || "")
      ? period
      : latest;
  }, null) || null;
}

const sameNumber = (left, right) => left == null || right == null
  ? left == null && right == null
  : Number.isFinite(Number(left)) && Number.isFinite(Number(right)) && Number(left) === Number(right);

function previousCalendarYear(period) {
  const [year, month, day] = period.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year - 1, month, 0)).getUTCDate();
  return `${String(year - 1).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(Math.min(day, lastDay)).padStart(2, "0")}`;
}

export function decodeFavoritesHistory(data, preset, snapshotRows) {
  if (!Object.prototype.hasOwnProperty.call(FAVORITES_HISTORY_URLS, preset)) {
    throw new Error(`Unknown mindSHARE Favorites preset: ${preset}`);
  }
  const expectedPeriod = snapshotLatestPeriod(snapshotRows);
  if (!expectedPeriod) throw new Error("Missing mindSHARE Cards reporting period");
  if (data?.schemaVersion !== 1 || data.kind !== "mindshare_favorites_history" || data.preset !== preset) {
    throw new Error("Unsupported mindSHARE Favorites export");
  }
  if (data.asOfPeriod !== expectedPeriod) {
    throw new Error(`mindSHARE Favorites period ${data.asOfPeriod} does not match Cards period ${expectedPeriod}`);
  }
  if (!Array.isArray(data.historyColumns) || data.historyColumns.length !== FAVORITES_HISTORY_COLUMNS.length ||
      data.historyColumns.some((column, index) => column !== FAVORITES_HISTORY_COLUMNS[index]) ||
      !Array.isArray(data.metadataColumns) || data.metadataColumns.length !== FAVORITES_METADATA_COLUMNS.length ||
      data.metadataColumns.some((column, index) => column !== FAVORITES_METADATA_COLUMNS[index])) {
    throw new Error("Invalid mindSHARE Favorites columns");
  }
  if (!Array.isArray(data.members) || !data.members.length ||
      !Array.isArray(data.metadataRows) || data.metadataRows.length !== data.members.length ||
      !Array.isArray(data.historyRows) || !Number.isInteger(data.historyRowCount) ||
      data.historyRowCount !== data.historyRows.length) {
    throw new Error("Incomplete mindSHARE Favorites rows");
  }
  if (preset === "top100" && data.members.length !== 100) {
    throw new Error("Current Top 100 export must contain exactly 100 members");
  }
  if (preset === "recent12m" &&
      (data.recentWindowStart !== previousCalendarYear(expectedPeriod) || data.recentWindowEnd !== expectedPeriod)) {
    throw new Error("Recent Releases export has an incompatible 12-month window");
  }
  const sourceHashes = data.sourceSHA256;
  const hashNames = ["game_stats", "metadata", "mindshare_cards"];
  if (!sourceHashes || hashNames.some(name => !/^[a-f0-9]{64}$/i.test(sourceHashes[name] || ""))) {
    throw new Error("Invalid mindSHARE Favorites source hashes");
  }

  const cardsByMetadataRow = new Map();
  for (const card of snapshotRows) {
    if (!Number.isInteger(card.metadataRow) || cardsByMetadataRow.has(card.metadataRow)) {
      throw new Error("Invalid or duplicate mindSHARE Cards metadataRow");
    }
    cardsByMetadataRow.set(card.metadataRow, card);
  }

  const metadataByRow = new Map();
  for (const metadata of data.metadataRows) {
    if (!Number.isInteger(metadata?.metadataRow) || typeof metadata.matchKey !== "string" ||
        !Array.isArray(metadata.values) || metadata.values.length !== data.metadataColumns.length ||
        metadataByRow.has(metadata.metadataRow)) {
      throw new Error("Invalid mindSHARE Favorites metadata row");
    }
    metadataByRow.set(metadata.metadataRow, metadata);
  }

  const memberKeys = new Set();
  const metadataGameToMember = new Map();
  const memberNamesByKey = new Map();
  const metadataGameIndex = data.metadataColumns.indexOf("Game");
  for (const member of data.members) {
    if (typeof member?.matchKey !== "string" || !member.matchKey || memberKeys.has(member.matchKey) ||
        typeof member.name !== "string" || !member.name || !Number.isInteger(member.metadataRow)) {
      throw new Error("Invalid or duplicate mindSHARE Favorites member");
    }
    const metadata = metadataByRow.get(member.metadataRow);
    const card = cardsByMetadataRow.get(member.metadataRow);
    const metadataName = metadata?.values[metadataGameIndex];
    if (!metadata || metadata.matchKey !== member.matchKey || !card ||
        typeof metadataName !== "string" || normalizeFavoriteMatchKey(metadataName) !== member.matchKey ||
        normalizeFavoriteMatchKey(member.name) !== member.matchKey ||
        card.matchKey !== member.matchKey || normalizeFavoriteMatchKey(card.name) !== member.matchKey ||
        card.metadataRow !== member.metadataRow ||
        member.latestPeriod !== card.latest_date ||
        !sameNumber(member.latestMindshare, card.latest_level) ||
        !sameNumber(member.latestWeeksToLaunch, card.latest_w2l)) {
      throw new Error(`mindSHARE Favorites member does not match Cards: ${member.matchKey}`);
    }
    if (preset === "upcoming" &&
        (!Number.isFinite(Number(member.latestWeeksToLaunch)) || Number(member.latestWeeksToLaunch) > -1)) {
      throw new Error(`Invalid Upcoming Releases member: ${member.matchKey}`);
    }
    const historyName = normalizeFavoriteMatchKey(metadataName);
    const existing = metadataGameToMember.get(historyName);
    if (existing && existing !== member.matchKey) {
      throw new Error(`Ambiguous mindSHARE Favorites metadata game: ${metadataName}`);
    }
    metadataGameToMember.set(historyName, member.matchKey);
    memberNamesByKey.set(member.matchKey, card.name);
    memberKeys.add(member.matchKey);
  }
  if (metadataByRow.size !== data.members.length) throw new Error("Unexpected mindSHARE Favorites metadata rows");

  const gameColumn = data.historyColumns.indexOf("Game");
  const historyRows = data.historyRows.map((values, rowIndex) => {
    if (!Array.isArray(values) || values.length !== data.historyColumns.length ||
        typeof values[gameColumn] !== "string" || !values[gameColumn].trim()) {
      throw new Error(`Invalid mindSHARE Favorites history row ${rowIndex}`);
    }
    const matchKey = metadataGameToMember.get(normalizeFavoriteMatchKey(values[gameColumn]));
    const canonicalName = memberNamesByKey.get(matchKey);
    if (!canonicalName) throw new Error(`Unmatched mindSHARE Favorites history game: ${values[gameColumn]}`);
    return Object.fromEntries(data.historyColumns.map((column, index) => [
      column,
      column === "Game" ? canonicalName : values[index],
    ]));
  });
  const historyIndex = buildHistoryIndex(historyRows);
  const expectedHistoryKeys = new Set([...memberNamesByKey.values()].map(normalize));
  if (historyIndex.size !== expectedHistoryKeys.size ||
      [...expectedHistoryKeys].some(key => !historyIndex.has(key))) {
    throw new Error("mindSHARE Favorites histories do not match exported membership");
  }

  const sourceSignature = hashNames.map(name => sourceHashes[name].toLowerCase()).join(":");
  const existingSignature = favoritesSourceVersions.get(expectedPeriod);
  if (existingSignature && existingSignature !== sourceSignature) {
    throw new Error(`Conflicting mindSHARE Favorites source versions for ${expectedPeriod}`);
  }
  favoritesSourceVersions.set(expectedPeriod, sourceSignature);
  return { data, historyIndex, asOfPeriod: expectedPeriod };
}

export function loadFavoriteHistory(preset, snapshotRows) {
  const url = FAVORITES_HISTORY_URLS[preset];
  if (!url) return Promise.reject(new Error(`Unknown mindSHARE Favorites preset: ${preset}`));
  const expectedPeriod = snapshotLatestPeriod(snapshotRows);
  if (!expectedPeriod) return Promise.reject(new Error("Missing mindSHARE Cards reporting period"));
  const key = `${preset}:${expectedPeriod}`;
  if (favoritesCache.has(key)) return Promise.resolve(favoritesCache.get(key));
  if (favoritesFlights.has(key)) return favoritesFlights.get(key);

  const start = performance.now();
  const request = fetch(url, { mode: "cors", cache: "no-cache" })
    .then(async response => {
      const text = await readGzipResponse(response);
      const contentLength = response.headers?.get?.("content-length");
      const compressedBytes = contentLength && Number.isFinite(Number(contentLength)) ? Number(contentLength) : null;
      if (compressedBytes !== null) recordMindshareValue(`favorites-transferred-bytes-${preset}`, compressedBytes);
      recordMindshareTiming("favorites-fetch-decode", start);
      const data = timeMindshare("favorites-json-parse", () => JSON.parse(text));
      const result = timeMindshare("favorites-adapt", () => decodeFavoritesHistory(data, preset, snapshotRows));
      const loaded = { ...result, compressedBytes };
      favoritesCache.set(key, loaded);
      return loaded;
    });
  const flight = request.finally(() => {
    if (favoritesFlights.get(key) === flight) favoritesFlights.delete(key);
  });
  favoritesFlights.set(key, flight);
  return flight;
}

export function loadHistory() {
  if (!historyFlight) {
    historyFlight = fetch(HISTORY_URL, { mode: "cors", cache: "no-cache" })
      .then(readGzipResponse).then(text => buildHistoryIndex(csvParse(text)))
      .catch(error => { historyFlight = undefined; throw error; });
  }
  return historyFlight;
}

function loadGameHistoryDocuments(names, allowMissing = false) {
  if (!manifestFlight) {
    manifestFlight = fetch(GAME_HISTORY_MANIFEST_URL, { cache: "no-cache" })
      .then(readGzipResponse).then(text => {
        const manifest = JSON.parse(text);
        if (manifest.schemaVersion !== 1 || !manifest.games || typeof manifest.sourceETag !== "string") {
          throw new Error("Invalid mindSHARE history manifest");
        }
        return manifest;
      }).finally(() => { manifestFlight = undefined; });
  }
  return manifestFlight.then(async manifest => {
    const entries = await Promise.all([...new Set(names.map(normalize))].map(async key => {
      const entry = manifest.games[key];
      if (!entry && allowMissing) return [key, null];
      if (!entry || !entry.key?.startsWith(`mindshare_history/${manifest.sourceETag}/`) ||
          !/^[a-zA-Z0-9_./-]+$/.test(entry.key) || entry.key.includes("..")) {
        throw new Error(`mindSHARE history unavailable for ${key}`);
      }
      if (!gameHistoryFlights.has(entry.key)) {
        const flight = fetch(`${GAME_HISTORY_BASE}/${entry.key}`, { cache: "no-cache" })
          .then(readGzipResponse).then(text => {
            const data = JSON.parse(text);
            if (data.schemaVersion !== 1 || data.matchKey !== key || !Array.isArray(data.rows) || data.rows.length !== entry.rowCount) {
              throw new Error(`Invalid mindSHARE history for ${key}`);
            }
            const index = buildHistoryIndex(data.rows);
            if (index.size !== 1 || !index.has(key)) throw new Error(`Mismatched mindSHARE history for ${key}`);
            gameHistoryIndexes.set(data, index.get(key));
            return data;
          }).catch(error => { gameHistoryFlights.delete(entry.key); throw error; });
        gameHistoryFlights.set(entry.key, flight);
      }
      return [key, await gameHistoryFlights.get(entry.key)];
    }));
    return new Map(entries);
  });
}

export async function loadGameHistories(names) {
  const documents = await loadGameHistoryDocuments(names);
  return new Map([...documents].map(([key, data]) => [key, gameHistoryIndexes.get(data)]));
}

export async function loadGameHistoryRows(name) {
  const documents = await loadGameHistoryDocuments([name], true);
  return documents.get(normalize(name))?.rows || [];
}

export function usesPerGameHistory({ viewMode, launchChartPreset }) {
  return viewMode === "trended" && launchChartPreset === "custom";
}

export function needsMindshareHistory({ viewMode, marketPeriod, comparisonGames, launchChartPreset, businessHistoryRequested, showAllGames }) {
  if (viewMode === "market" && marketPeriod === "1w") return false;
  return !["trended", "business"].includes(viewMode) ||
    (viewMode === "business" && businessHistoryRequested === true) ||
    (viewMode === "trended" && (showAllGames === true || comparisonGames.length > 0 || launchChartPreset !== "custom"));
}
